import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CustomerProductName } from 'src/constants/customer/enums'
import {
  CUSTOMER_PRODUCT_NAMES,
  customerProductLabels,
} from 'src/constants/customer-product/constants'
import type { CustomerProduct } from 'src/interfaces/customerProduct'

// This app registers no dayjs plugins globally — App.tsx wires only MUI's AdapterDayjs — and
// reading these timestamps requires a UTC parse (see formatCustomerProductTimestamp).
// `extend` is idempotent, so importing this module more than once is harmless.
dayjs.extend(utc)

const DISPLAY_FORMAT = 'MMM D, YYYY HH:mm'

/** A member of the backend's own `DATE_FORMATS`, with no offset. */
const WIRE_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/**
 * In-memory search over the products list. Returns the input array by reference for an empty
 * query so an untouched search box does not churn the caller's `useMemo`. Never mutates.
 */
export const filterCustomerProducts = (
  products: CustomerProduct[],
  search: string,
): CustomerProduct[] => {
  const normalizedSearch = search.trim().toLowerCase()

  if (normalizedSearch === '') {
    return products
  }

  return products.filter((product) => {
    const haystack = [customerProductLabels[product.product], product.id]

    return haystack.some((value) => {
      return value.toLowerCase().includes(normalizedSearch)
    })
  })
}

/**
 * Renders a backend product timestamp for display.
 *
 * Parsed as UTC on purpose. The columns are pg `timestamp` WITHOUT timezone holding
 * wall-clock digits that the write DTOs relabelled as UTC via `moment(...).utc(true)`; the
 * response carries those digits with a `Z` suffix. Parsing as local time would shift them by
 * the admin's own offset, so every rendered time would be wrong by that amount.
 */
export const formatCustomerProductTimestamp = (value: string | null): string | null => {
  if (!value) {
    return null
  }

  const parsed = dayjs.utc(value)

  if (!parsed.isValid()) {
    return null
  }

  return parsed.format(DISPLAY_FORMAT)
}

/**
 * Serializes a picker value for a create or update request.
 *
 * Deliberately NOT the inverse of `formatCustomerProductTimestamp`: this emits the picked
 * wall-clock digits with no offset, because the backend applies `.utc(true)`, which relabels
 * rather than converts. Any offset-bearing format (`toISOString()`, `format()` with `Z`)
 * would shift the stored value by the admin's timezone on every save.
 */
export const serializeCustomerProductTimestamp = (value: Dayjs): string => {
  return value.format(WIRE_FORMAT)
}

/**
 * Whether a product is active *right now*, as opposed to merely ongoing.
 *
 * The two are not the same, and the difference is load-bearing for feature gates. The GET
 * this list comes from filters only on `endTime` (null or in the future), so **future-dated
 * rows are included** — a product that starts next month is in the list today. The backend's
 * own gate is `PartnerProductService.getActive`, which additionally requires
 * `startTime <= now`. Reading "present in the list" as "entitled" therefore opens a gate the
 * API will refuse.
 *
 * Mirrors `getActive` exactly:
 * - compared in UTC, because the stored digits are wall-clock relabelled as UTC (see
 *   `formatCustomerProductTimestamp`) and the backend compares them against `moment().utc()`
 * - a null `startTime` is NOT active: the backend's `LessThanOrEqual(now)` is SQL, where
 *   `NULL <= now` is unknown and the row drops out
 * - `endTime` needs no check here — the endpoint has already excluded ended rows
 */
export const isCustomerProductActive = (product: CustomerProduct): boolean => {
  if (!product.startTime) {
    return false
  }

  const startTime = dayjs.utc(product.startTime)

  if (!startTime.isValid()) {
    return false
  }

  return !startTime.isAfter(dayjs.utc())
}

/**
 * The product names that can still be added. `POST /products` 409s when the product is
 * already active, so the Add form only ever offers what is absent from the list.
 */
export const getAvailableProductsToAdd = (products: CustomerProduct[]): CustomerProductName[] => {
  const activeNames = products.map((product) => {
    return product.product
  })

  return CUSTOMER_PRODUCT_NAMES.filter((name) => {
    return !activeNames.includes(name)
  })
}
